(() => {
  'use strict';
  if (window.LRSWorldConnections) return;
  // Translate display text only; all map coordinates, IDs and routing inputs stay canonical.
  const i18n = window.LRSI18n;
  const t = (text, params = {}) => i18n ? i18n.t(text, params) : String(text).replace(/\{([A-Za-z][A-Za-z0-9_]*)\}/g, (match, key) => Object.prototype.hasOwnProperty.call(params, key) ? String(params[key]) : match);
  const placeName = name => i18n ? i18n.place(name) : String(name || '');
  const countryName = (code, label) => i18n ? i18n.country(code, label) : String(label || code || '');
  const instances = new Map();
  const catalogs = new Map();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = window.matchMedia('(max-width: 700px)');
  const motionAllowed = () => !reducedMotion.matches && document.body?.dataset.lrsMotion !== 'false';
  const ns = 'http://www.w3.org/2000/svg';
  const world = [0, 0, 1200, 600];
  const regionNames = {europe: 'Europe', usa: 'United States', canada: 'Canada', japan: 'Japan'};
  const regionKeys = Object.keys(regionNames);
  const searchText = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const exclusions = value => new Set(String(value || '').toUpperCase().split(/[\s,;]+/).filter(Boolean));

  function getCatalog(url) {
    if (!catalogs.has(url)) catalogs.set(url, fetch(url, {credentials: 'same-origin'}).then(response => {
      if (!response.ok) throw new Error('Map catalog unavailable');
      return response.json();
    }));
    return catalogs.get(url);
  }

  // Natural Earth I, matching the bundled public-domain land geometry.
  function project(longitude, latitude) {
    const l = longitude * Math.PI / 180;
    const p = latitude * Math.PI / 180;
    const p2 = p * p;
    const p4 = p2 * p2;
    return [
      600 + 205 * l * (.8707 - .131979 * p2 + p4 * (-.013791 + p4 * (.003971 * p2 - .001529 * p4))),
      310 - 205 * p * (1.007226 + p2 * (.015085 + p4 * (-.044475 + .028874 * p2 - .005916 * p4)))
    ];
  }

  // An intentionally quiet Atlantic-only travel corridor, projected once to the
  // same map coordinates as the routes. The visible land path still determines
  // the actual coastline. This basin mask excludes the Mediterranean, Channel,
  // Irish/North/Baltic seas, Canadian inland waters, Arctic and Pacific.
  const atlanticBasin = [
    [-82, 24], [-82, 30], [-79, 35], [-77, 37], [-76, 40], [-72, 43],
    [-67, 45.5], [-62.5, 46], [-60, 48], [-59, 52], [-62, 59.5],
    [-62, 63], [-57, 66.5], [-25, 66.5], [-12, 64], [-6.2, 61],
    [-6.2, 55.5], [-10.8, 55.5], [-11, 51], [-5.6, 50], [-5, 48.5],
    [-1, 46], [-1, 44], [-8, 43], [-7.5, 37], [-6, 36], [-6, 35],
    [-10, 30], [-20, 0], [-60, 0], [-60, 14], [-61, 18],
    [-68, 20], [-75, 21], [-79, 24]
  ].map(([longitude, latitude]) => project(longitude, latitude));

  function inAtlantic(point) {
    const [x, y] = point;
    let inside = false;
    for (let i = 0, j = atlanticBasin.length - 1; i < atlanticBasin.length; j = i++) {
      const [xi, yi] = atlanticBasin[i], [xj, yj] = atlanticBasin[j];
      if ((yi > y) !== (yj > y) && x < (xj - xi) * (y - yi) / (yj - yi) + xi) inside = !inside;
    }
    return inside;
  }

  function coordinate(value, limit) {
    if (!['string', 'number'].includes(typeof value) || !/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(String(value).trim())) return null;
    const number = Number(value);
    return Number.isFinite(number) && Math.abs(number) <= limit ? number : null;
  }

  function svgElement(name, attributes) {
    const element = document.createElementNS(ns, name);
    Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
    return element;
  }

  const clamp = (value, low = 0, high = 1) => Math.max(low, Math.min(high, value));
  const smoothstep = value => { const t = clamp(value); return t * t * (3 - 2 * t); };

  function routePoint(origin, route, t) {
    if (route.segments?.length) {
      const position = clamp(t) * route.segments.length;
      const index = Math.min(route.segments.length - 1, Math.floor(position));
      const segment = route.segments[index];
      const local = position - index;
      const a = (1 - local) * (1 - local), b = 2 * (1 - local) * local, c = local * local;
      return [a * segment.from[0] + b * segment.control[0] + c * segment.to[0], a * segment.from[1] + b * segment.control[1] + c * segment.to[1]];
    }
    const a = (1 - t) * (1 - t), b = 2 * (1 - t) * t, c = t * t;
    return [a * origin[0] + b * route.control[0] + c * route.point[0], a * origin[1] + b * route.control[1] + c * route.point[1]];
  }

  // A schematic maritime connection for Japan, rather than an overland arc
  // across Asia. Quiet rounded bends follow the Mediterranean, Suez and Indian
  // Ocean. These waypoints illustrate a connection, not a carrier itinerary.
  function japanJourney(origin, place, point) {
    const sea = [[23.25, 39.8], [24.3, 38.6], [25.3, 36.1], [28.0, 34.3], [31.5, 31.8], [32.3, 31.0], [32.6, 29.8], [35.5, 23.0], [43.5, 12.5], [52.0, 12.0], [72.0, 5.0], [81.0, 5.1], [93.0, 5.0], [98.0, 5.0], [103.4, 1.3], [106.0, 3.0], [113.0, 10.0], [120.0, 21.0], [125.0, 25.0], [130.0, 28.0]];
    if (place.lon > 138) sea.push([135.0, 31.0], [139.6, 34.3], [139.8, 35.2], [139.7, 35.45]);
    else if (place.lon > 136) sea.push([134.0, 31.0], [136.8, 34.0], [136.85, 34.7]);
    else sea.push([133.0, 31.0], [135.2, 33.5], [134.9, 34.2], [135.0, 34.55]);
    const waypoints = [origin, ...sea.map(([lon, lat]) => project(lon, lat)), point];
    const segments = [];
    const middle = (a, b) => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    let cursor = origin;
    let pathData = `M${origin[0]},${origin[1]}`;
    for (let index = 1; index < waypoints.length - 1; index++) {
      const before = waypoints[index - 1], corner = waypoints[index], after = waypoints[index + 1];
      const entry = corner.map((value, axis) => value + (before[axis] - value) * .14);
      const exit = corner.map((value, axis) => value + (after[axis] - value) * .14);
      segments.push({from: cursor, control: middle(cursor, entry), to: entry}, {from: entry, control: corner, to: exit});
      pathData += ` L${entry[0]},${entry[1]} Q${corner[0]},${corner[1]} ${exit[0]},${exit[1]}`;
      cursor = exit;
    }
    segments.push({from: cursor, control: middle(cursor, point), to: point});
    pathData += ` L${point[0]},${point[1]}`;
    return {market: 'japan', waypoints, segments, pathData};
  }

  // Use the same coastline that visitors see, in its original projected coordinates.
  // Classification is performed once per journey, never during animation frames.
  function landClassifier(svg) {
    const land = svg.querySelector('.lrs-world-land');
    if (!land) return null;
    if (typeof land.isPointInFill === 'function') {
      const point = typeof svg.createSVGPoint === 'function' ? svg.createSVGPoint() : {x: 0, y: 0};
      try {
        land.isPointInFill(point);
        return (x, y) => { point.x = x; point.y = y; return land.isPointInFill(point); };
      } catch (_) { /* Try the equivalent canvas geometry in older SVG implementations. */ }
    }
    if (typeof Path2D !== 'undefined') {
      try {
        const context = document.createElement('canvas').getContext('2d');
        const path = new Path2D(land.getAttribute('d') || '');
        if (context) return (x, y) => context.isPointInPath(path, x, y, land.getAttribute('fill-rule') || 'nonzero');
      } catch (_) { /* The route remains usable with its Land Rover marker. */ }
    }
    return null;
  }

  function journeyProfile(origin, route, isLand) {
    if (route.profile) return route.profile;
    const estimate = route.waypoints ? route.waypoints.slice(1).reduce((sum, point, index) => sum + Math.hypot(point[0] - route.waypoints[index][0], point[1] - route.waypoints[index][1]), 0) : Math.hypot(route.control[0] - origin[0], route.control[1] - origin[1]) + Math.hypot(route.point[0] - route.control[0], route.point[1] - route.control[1]);
    const steps = Math.max(80, Math.min(route.market === 'japan' ? 2400 : 1200, Math.ceil(estimate * (route.market === 'japan' ? 3 : 2))));
    const samples = [{t: 0, point: origin, distance: 0}];
    for (let index = 1; index <= steps; index++) {
      const point = routePoint(origin, route, index / steps);
      const previous = samples[index - 1];
      samples.push({t: index / steps, point, distance: previous.distance + Math.hypot(point[0] - previous.point[0], point[1] - previous.point[1])});
    }
    const length = samples[samples.length - 1].distance;
    // Coastal destination coordinates can fall just beyond the generalized 1:110m
    // shoreline. A tiny map-unit anchor preserves land departure and arrival.
    const anchor = Math.min(1.25, length * .08);
    // Japan has its own explicit maritime journey. Existing destinations retain
    // the Atlantic-only ferry rule; no other eastbound markets are introduced.
    // Both the ocean mask and visible coastline are evaluated only while caching
    // the route profile; animation frames perform no geographic classification.
    const japanSea = route.market === 'japan' && route.segments?.length;
    const onLand = (point, distance) => distance <= anchor || length - distance <= anchor || (!japanSea && (!isLand || !inAtlantic(point) || isLand(point[0], point[1])));
    const transitions = [];
    let wasLand = true;
    samples.forEach((sample, index) => {
      const land = onLand(sample.point, sample.distance);
      if (index && land !== wasLand) {
        let low = samples[index - 1].t, high = sample.t;
        const previous = samples[index - 1];
        for (let refinement = 0; refinement < 9; refinement++) {
          const middle = (low + high) / 2;
          const point = routePoint(origin, route, middle);
          const distance = previous.distance + (sample.distance - previous.distance) * ((middle - previous.t) / (sample.t - previous.t));
          if (onLand(point, distance) === wasLand) low = middle; else high = middle;
        }
        const t = (low + high) / 2;
        const distance = previous.distance + (sample.distance - previous.distance) * ((t - previous.t) / (sample.t - previous.t));
        transitions.push({progress: distance / Math.max(.001, length), land});
      }
      wasLand = land;
    });
    const seas = [];
    let start = null;
    transitions.forEach(transition => {
      if (!transition.land) start = transition.progress;
      else if (start !== null) { seas.push([start, transition.progress]); start = null; }
    });
    if (start !== null) seas.push([start, 1]);
    route.profile = {samples, length, seas};
    return route.profile;
  }

  function journeyPosition(profile, origin, route, progress) {
    const distance = progress * profile.length;
    let low = 0, high = profile.samples.length - 1;
    while (low + 1 < high) {
      const middle = (low + high) >> 1;
      if (profile.samples[middle].distance < distance) low = middle; else high = middle;
    }
    const from = profile.samples[low], to = profile.samples[high];
    const fraction = (distance - from.distance) / Math.max(.001, to.distance - from.distance);
    const t = from.t + (to.t - from.t) * fraction;
    const point = routePoint(origin, route, t);
    const before = routePoint(origin, route, Math.max(0, t - .0001));
    const after = routePoint(origin, route, Math.min(1, t + .0001));
    const dx = after[0] - before[0];
    const dy = after[1] - before[1];
    const direction = dx >= 0 ? -1 : 1; // The supplied Land Rover faces left.
    const heading = clamp(Math.atan2(dy * -direction, dx * -direction) * 180 / Math.PI, -22, 22);
    return {point, direction, heading};
  }

  // Japan is an explicit expansion market. All other eastern-map restrictions
  // continue to apply to catalog, fallback and editor-authored destinations.
  const easternMapLimit = 26.6;
  const excludedMarkets = new Set(['by', 'blr', 'belarus', 'belorussia', 'weissrussland', 'weißrussland', 'беларусь', 'белоруссия', 'ru', 'rus', 'russia', 'russian federation', 'russland', 'россия', 'ua', 'ukr', 'ukraine', 'украина', 'украіна']);
  const greekMarkets = new Set(['gr', 'grc', 'greece', 'griechenland', 'hellas', 'ελλαδα']);
  const japaneseMarkets = new Set(['jp', 'jpn', 'japan', '日本']);
  function withinMapScope(place, country = place.country || '') {
    const market = searchText(country).trim();
    const longitude = coordinate(place.lon, 180);
    const latitude = coordinate(place.lat, 90);
    const japan = japaneseMarkets.has(market) && longitude >= 122 && longitude <= 146 && latitude >= 24 && latitude <= 46;
    return longitude !== null && !excludedMarkets.has(market) && (longitude <= easternMapLimit || greekMarkets.has(market) || japan);
  }

  function validPlace(item) {
    return item && typeof item.id === 'string' && typeof item.label === 'string' && coordinate(item.lat, 90) !== null && coordinate(item.lon, 180) !== null;
  }

  function init(root = document) {
    const sections = [...root.querySelectorAll('[data-world-connections]')];
    if (root.matches?.('[data-world-connections]')) sections.unshift(root);
    sections.forEach(section => {
      if (instances.has(section)) return;
      const $ = selector => section.querySelector(selector);
      const svg = $('[data-map-svg]');
      const viewport = $('[data-map-viewport]');
      const routeLayer = $('[data-map-routes]');
      const travelerLayer = $('[data-map-travelers]');
      const pointLayer = $('[data-map-points]');
      const controls = $('[data-map-controls]');
      const pause = $('[data-map-pause]');
      const pauseLabel = $('[data-map-pause-label]');
      const selectedCity = $('[data-map-selected-city]');
      const selectedCountry = $('[data-map-selected-country]');
      const live = $('[data-map-live]');
      const originDot = $('[data-map-origin-dot]');
      const originRing = $('[data-map-origin-ring]');
      const originLabel = $('[data-map-origin-label]');
      const explorer = $('[data-map-explorer]');
      const browser = $('[data-map-browser]');
      const worldIntro = $('[data-map-world-intro]');
      const search = $('[data-map-search]');
      const searchLabel = $('[data-map-search-label]');
      const results = $('[data-map-result-count]');
      const placeList = $('[data-map-place-list]');
      const detailLabel = $('[data-map-detail-label]');
      const detailCities = $('[data-map-detail-cities]');
      const detailNote = $('[data-map-detail-note]');
      const fallback = $('[data-map-fallback]');
      const nightPaths = [...section.querySelectorAll('[data-map-night-altitude]')];
      const localTime = $('[data-map-local-time]');
      if (![svg, viewport, routeLayer, travelerLayer, pointLayer, controls, pause, pauseLabel, selectedCity, selectedCountry, live, originDot, originRing, originLabel].every(Boolean)) return;

      const originLongitude = coordinate(section.dataset.originLongitude, 180);
      const originLatitude = coordinate(section.dataset.originLatitude, 90);
      if (originLongitude === null || originLatitude === null) return;
      const abort = new AbortController();
      let routeAbort = new AbortController();
      const listen = (target, event, handler, options = {}) => target.addEventListener(event, handler, {...options, signal: abort.signal});
      const origin = project(originLongitude, originLatitude);
      const originName = section.dataset.origin || 'Thessaloniki';
      originLabel.textContent = placeName(originName);
      const duration = Math.min(30, Math.max(8, Number(section.dataset.duration) || 18)) * 1000;
      const markerSize = Math.min(44, Math.max(20, Number(section.dataset.markerSize) || 28));
      const markerUrl = section.dataset.markerUrl;
      const isLand = landClassifier(svg);
      const animationEnabled = section.dataset.animation !== 'false';
      const viewButtons = [...section.querySelectorAll('[data-map-view]')];
      const manual = [];
      let routes = [];
      let selected = null;
      let highlighted = null;
      let catalog = null;
      let selectedPlace = null;
      let view = 'world';
      let camera = [...world];
      let cameraTween = null;
      let userPaused = false;
      let inViewport = typeof IntersectionObserver === 'undefined';
      let frame = 0;
      let previousTime = 0;
      let elapsed = 0;
      let journey = null;
      let journeyElapsed = 0;
      let traveler = null;
      let travelerCar = null;
      let travelerFerry = null;
      let journeyTrace = null;
      let previousRegion = null;
      let regionBag = [];
      let journeyBags = {europe: [], usa: [], canada: [], japan: []};
      let ambient = [];
      let visibleRoutes = new Set();
      let nextLabelAt = 1800;
      let clockInterval = 0;
      const ambientEnabled = section.dataset.ambientLabels !== 'false';
      const dayNightEnabled = section.dataset.showDayNight !== 'false';
      const departureFade = 700;
      const arrivalHold = 1000;
      const fadeDuration = 1200;
      const journeyPause = 1400;
      const labelLifetime = 11000;
      let lastDraw = 0;
      let viewportWidth = 1200;
      let viewportHeight = 600;
      let destroyed = false;
      let markerFailed = false;

      for (const button of section.querySelectorAll('[data-map-city]')) {
        const lon = coordinate(button.dataset.longitude, 180);
        const lat = coordinate(button.dataset.latitude, 90);
        const label = (button.dataset.label || '').trim();
        if (lon === null || lat === null || !label) { button.setAttribute('aria-disabled', 'true'); continue; }
        const place = {id: button.dataset.blockId, button, lon, lat, label, country: button.dataset.country || '', region: regionKeys.includes(button.dataset.region) ? button.dataset.region : 'europe'};
        if (!withinMapScope(place)) { button.hidden = true; button.style.display = 'none'; continue; }
        manual.push(place);
        const labelNode = button.querySelector('span:not([aria-hidden]):not(.lrs-world-live)');
        if (labelNode) labelNode.textContent = placeName(label);
        const countryNode = button.querySelector('.lrs-world-live');
        if (countryNode) countryNode.textContent = `, ${placeName(place.country)}`;
        button.disabled = false;
        button.removeAttribute('aria-disabled');
        listen(button, 'click', () => {
          if (!routes.some(route => route.id === place.id)) changeView(place.region, true);
          select(routes.find(route => route.id === place.id), true);
        });
        listen(button, 'focus', () => highlight(routes.find(route => route.id === place.id) || selected));
        listen(button, 'blur', () => highlight(null));
        listen(button, 'pointerenter', event => { if (event.pointerType !== 'touch') highlight(routes.find(route => route.id === place.id) || selected); });
        listen(button, 'pointerleave', () => highlight(null));
      }

      function screenUnit() { return 1 / Math.max(.01, Math.min(viewportWidth / camera[2], viewportHeight / camera[3])); }

      function sizeMarkers() {
        const unit = screenUnit();
        routes.forEach(route => {
          route.dot.setAttribute('r', 2.8 * unit);
          route.hit.setAttribute('r', 18 * unit);
          route.labelElement.style.fontSize = `${(mobile.matches ? 12 : 13) * unit}px`;
          route.labelElement.style.strokeWidth = `${4 * unit}px`;
        });
        originDot.setAttribute('cx', origin[0]);
        originDot.setAttribute('cy', origin[1]);
        originDot.setAttribute('r', 4.2 * unit);
        originRing.setAttribute('cx', origin[0]);
        originRing.setAttribute('cy', origin[1]);
        originRing.setAttribute('r', 10 * unit);
        const originLeft = origin[0] + (placeName(originName).length * (i18n?.isJapanese ? 13 : 8) + 22) * unit > camera[0] + camera[2];
        originLabel.setAttribute('text-anchor', originLeft ? 'end' : 'start');
        originLabel.setAttribute('x', origin[0] + (originLeft ? -14 : 14) * unit);
        originLabel.setAttribute('y', origin[1] + 4 * unit);
        originLabel.style.fontSize = `${(mobile.matches ? 12 : 13) * unit}px`;
        originLabel.style.strokeWidth = `${4 * unit}px`;
        drawScene();
      }

      function shuffle(items) {
        const result = [...items];
        for (let i = result.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [result[i], result[j]] = [result[j], result[i]];
        }
        return result;
      }

      function nextJourney() {
        const available = routes.filter(route => !route.isOrigin);
        if (!available.length) return null;
        const regions = regionKeys.filter(region => available.some(route => route.region === region));
        if (!regionBag.length) regionBag = shuffle(regions);
        if (regionBag.length > 1 && regionBag[regionBag.length - 1] === previousRegion) {
          [regionBag[0], regionBag[regionBag.length - 1]] = [regionBag[regionBag.length - 1], regionBag[0]];
        }
        const region = regionBag.pop();
        previousRegion = region;
        if (!journeyBags[region].length) journeyBags[region] = shuffle(available.filter(route => route.region === region));
        // Each available region appears once per shuffled round; destinations have their own shuffle bags.
        let next = journeyBags[region].pop();
        if (next === journey && journeyBags[region].length) {
          journeyBags[region].unshift(next);
          next = journeyBags[region].pop();
        }
        return next;
      }

      function beginJourney(route = nextJourney()) {
        journey = route || null;
        if (journey) {
          journeyProfile(origin, journey, isLand);
          journeyTrace?.setAttribute('d', journey.path.getAttribute('d'));
          previousRegion = journey.region;
          regionBag = regionBag.filter(region => region !== journey.region);
          journeyBags[journey.region] = journeyBags[journey.region].filter(item => item !== journey);
        }
        journeyElapsed = 0;
        ambient = ambient.filter(item => item.route !== journey);
        section.dataset.mapJourney = journey?.id || '';
        drawScene();
      }

      function opacityEnvelope(time, lifetime, fade = 2000) {
        return Math.max(0, Math.min(1, time / fade, (lifetime - time) / fade));
      }

      function labelBox(route, occupied) {
        const unit = screenUnit();
        const font = mobile.matches ? 12 : 13;
        const width = (placeName(route.label).length * font * (i18n?.isJapanese ? 1 : .6) + 12) * unit;
        const height = (font + 8) * unit;
        const gap = 11 * unit;
        const x = route.point[0], y = route.point[1];
        const candidates = [
          {x: x + gap, y: y - gap, anchor: 'start'},
          {x: x - gap, y: y - gap, anchor: 'end'},
          {x: x + gap, y: y + height, anchor: 'start'},
          {x: x - gap, y: y + height, anchor: 'end'}
        ];
        for (const candidate of candidates) {
          const box = {left: candidate.x - (candidate.anchor === 'end' ? width : 0), right: candidate.x + (candidate.anchor === 'end' ? 0 : width), top: candidate.y - height, bottom: candidate.y + 3 * unit};
          if (box.left < camera[0] + 8 * unit || box.right > camera[0] + camera[2] - 8 * unit || box.top < camera[1] + 8 * unit || box.bottom > camera[1] + camera[3] - 8 * unit) continue;
          if (occupied.some(other => box.left < other.right + 5 * unit && box.right > other.left - 5 * unit && box.top < other.bottom + 5 * unit && box.bottom > other.top - 5 * unit)) continue;
          route.labelElement.setAttribute('x', candidate.x);
          route.labelElement.setAttribute('y', candidate.y);
          route.labelElement.setAttribute('text-anchor', candidate.anchor);
          return box;
        }
        return null;
      }

      function labelOccupancy() {
        const unit = screenUnit();
        const x = Number(originLabel.getAttribute('x'));
        const y = Number(originLabel.getAttribute('y'));
        const width = (placeName(originName).length * (mobile.matches ? 12 : 13) * (i18n?.isJapanese ? 1 : .64) + 14) * unit;
        const left = originLabel.getAttribute('text-anchor') === 'end';
        return [{left: left ? x - width : origin[0] - 12 * unit, right: left ? origin[0] + 12 * unit : x + width, top: y - 20 * unit, bottom: y + 10 * unit}];
      }

      function addAmbientLabel() {
        const max = mobile.matches ? 1 : 2;
        if (!ambientEnabled || ambient.length >= max) return;
        const occupied = labelOccupancy();
        if (journey) { const box = labelBox(journey, occupied); if (box) occupied.push(box); }
        ambient.forEach(item => { const box = labelBox(item.route, occupied); if (box) occupied.push(box); });
        const candidate = shuffle(routes.filter(route => !route.isOrigin && route !== journey && !ambient.some(item => item.route === route))).find(route => labelBox(route, occupied));
        if (candidate) ambient.push({route: candidate, start: elapsed});
      }

      function drawScene() {
        if (!traveler) return;
        const unit = screenUnit();
        const staticScene = !motionAllowed() || !animationEnabled;
        const activeOpacity = staticScene ? 1 : journeyElapsed <= duration + arrivalHold
          ? .35 + .65 * smoothstep(journeyElapsed / departureFade)
          : 1 - smoothstep((journeyElapsed - duration - arrivalHold) / fadeDuration);
        const active = new Map();
        if (journey) active.set(journey, activeOpacity);
        if (highlighted && highlighted !== journey) active.set(highlighted, 1);
        const maxLabels = mobile.matches ? 2 : 3;
        if (!staticScene && ambientEnabled) ambient.slice(0, Math.max(0, maxLabels - active.size)).forEach(item => active.set(item.route, opacityEnvelope(elapsed - item.start, labelLifetime)));
        const occupied = labelOccupancy();
        const currentRoutes = new Set(active.keys());
        new Set([...visibleRoutes, ...currentRoutes]).forEach(route => {
          const routeOpacity = route === journey ? activeOpacity * .2 : route === highlighted ? .48 : 0;
          route.path.style.opacity = String(routeOpacity);
          route.routeHit.style.pointerEvents = routeOpacity > .1 ? 'stroke' : 'none';
          route.dot.style.opacity = String(active.get(route) || 0);
          route.hit.style.pointerEvents = (active.get(route) || 0) > .2 ? 'all' : 'none';
          route.labelElement.style.opacity = '0';
        });
        visibleRoutes = currentRoutes;
        active.forEach((opacity, route) => {
          const box = labelBox(route, occupied);
          if (box) { occupied.push(box); route.labelElement.style.opacity = String(opacity); }
        });
        traveler.style.display = journey && !journey.isOrigin && activeOpacity > 0 ? '' : 'none';
        traveler.style.opacity = String(activeOpacity * .94);
        if (journey && !journey.isOrigin) {
          const progress = staticScene ? 1 : Math.min(1, journeyElapsed / duration);
          const profile = journey.profile;
          const {point, direction, heading} = journeyPosition(profile, origin, journey, progress);
          const blend = Math.min(.022, 360 / duration);
          let sea = 0;
          profile.seas.forEach(([start, end]) => {
            // Crossfade inside the cached sea segment: Atlantic journeys retain
            // their coastline boundary; Japan uses its explicit maritime path.
            const edge = Math.min(blend, (end - start) / 2);
            if (edge > 0) sea = Math.max(sea, smoothstep((progress - start) / edge) * (1 - smoothstep((progress - end + edge) / edge)));
          });
          travelerCar.style.opacity = String(1 - sea);
          travelerFerry.style.opacity = String(sea);
          traveler.dataset.mapProgress = progress.toFixed(4);
          traveler.dataset.mapTerrain = sea < .01 ? 'land' : sea > .99 ? 'sea' : 'coast';
          traveler.setAttribute('transform', `translate(${point[0]} ${point[1]}) rotate(${heading}) scale(${direction * unit} ${unit})`);
          journeyTrace.setAttribute('stroke-dasharray', `${Math.max(.00001, progress)} 1`);
          journeyTrace.style.opacity = String(activeOpacity * .7);
        } else {
          journeyTrace.style.opacity = '0';
        }
      }

      function refreshClock() {
        const now = new Date();
        if (localTime) {
          localTime.textContent = t('Your time {time}', {time: new Intl.DateTimeFormat(i18n?.isJapanese ? 'ja' : undefined, {hour: '2-digit', minute: '2-digit', hourCycle: 'h23'}).format(now)});
          localTime.setAttribute('datetime', now.toISOString());
          localTime.hidden = !dayNightEnabled;
        }
        if (dayNightEnabled && window.LRSMapSolar) nightPaths.forEach(path => {
          path.setAttribute('d', window.LRSMapSolar.nightPath(now, Number(path.dataset.mapNightAltitude)));
        });
        nightPaths.forEach(path => { path.style.display = dayNightEnabled ? '' : 'none'; });
      }

      function refreshClockSchedule() {
        if (clockInterval) clearInterval(clockInterval);
        clockInterval = 0;
        if (inViewport && !document.hidden && !destroyed) {
          refreshClock();
          clockInterval = setInterval(refreshClock, 60000);
        }
      }

      function renderRoutes(places) {
        routeAbort.abort();
        routeAbort = new AbortController();
        routeLayer.replaceChildren(); pointLayer.replaceChildren(); travelerLayer.replaceChildren();
        routes = []; selected = null; highlighted = null; journey = null;
        journeyBags = {europe: [], usa: [], canada: [], japan: []}; regionBag = []; ambient = []; visibleRoutes = new Set(); elapsed = 0; nextLabelAt = 1800;
        journeyTrace = svgElement('path', {class: 'lrs-world-journey-trace', 'pathLength': '1', 'stroke-dasharray': '0 1'});
        routeLayer.append(journeyTrace);
        traveler = svgElement('g', {class: 'lrs-world-traveler'});
        travelerCar = svgElement('g', {class: 'lrs-world-traveler-car'});
        travelerFerry = svgElement('g', {class: 'lrs-world-traveler-ferry'});
        const carWidth = mobile.matches ? Math.max(20, markerSize - 2) : markerSize;
        const imageFrame = svgElement('svg', {x: -carWidth / 2, y: -carWidth * .5612 / 2, width: carWidth, height: carWidth * .5612, viewBox: section.dataset.markerCrop || '63 237 376 211', preserveAspectRatio: 'xMidYMid meet', overflow: 'hidden'});
        const image = svgElement('image', {width: section.dataset.markerWidth || 500, height: section.dataset.markerHeight || 625, href: markerUrl || '', preserveAspectRatio: 'none'});
        imageFrame.append(image);
        const fallbackDot = svgElement('circle', {r: 2, class: 'lrs-world-traveler-fallback'});
        imageFrame.style.display = markerFailed || !markerUrl ? 'none' : '';
        fallbackDot.style.display = markerFailed || !markerUrl ? '' : 'none';
        image.addEventListener('error', () => { markerFailed = true; imageFrame.style.display = 'none'; fallbackDot.style.display = ''; }, {once: true, signal: routeAbort.signal});
        travelerCar.append(imageFrame, fallbackDot);
        // A small left-facing ferry, with the same quiet monochrome weight as the car.
        travelerFerry.setAttribute('transform', `scale(${carWidth / 30})`);
        travelerFerry.append(
          svgElement('path', {class: 'lrs-world-ferry-wake', d: 'M14 5h7 M15 8h10'}),
          svgElement('path', {class: 'lrs-world-ferry-hull', d: 'M-15 1.5H14L10 7H-9Z'}),
          svgElement('path', {class: 'lrs-world-ferry-deck', d: 'M-9 1.5v-4h17v4 M-4-2.5v-4h8v4 M2-6.5v-2'}),
          svgElement('path', {class: 'lrs-world-ferry-windows', d: 'M-5-.5h1 M-1-.5h1 M3-.5h1 M-1-4.5h2'})
        );
        traveler.append(travelerCar, travelerFerry); travelerLayer.append(traveler);
        for (const place of places) {
          const point = project(place.lon, place.lat);
          const distance = Math.hypot(point[0] - origin[0], point[1] - origin[1]);
          const isOrigin = distance < .5;
          const control = [(origin[0] + point[0]) / 2, (origin[1] + point[1]) / 2 - Math.min(100, distance * .3)];
          const maritime = place.region === 'japan' && japaneseMarkets.has(searchText(place.country).trim()) ? japanJourney(origin, place, point) : null;
          const pathData = maritime?.pathData || `M${origin[0]},${origin[1]} Q${control[0]},${control[1]} ${point[0]},${point[1]}`;
          const path = svgElement('path', {class: 'lrs-world-route', d: pathData});
          const routeHit = svgElement('path', {class: 'lrs-world-route-hit', d: pathData});
          routeHit.style.pointerEvents = 'none';
          const dot = svgElement('circle', {class: 'lrs-world-point', cx: point[0], cy: point[1], r: 3});
          const hit = svgElement('circle', {class: 'lrs-world-point-hit', cx: point[0], cy: point[1], r: 18});
          hit.style.pointerEvents = 'none';
          const labelElement = svgElement('text', {class: 'lrs-world-point-label', x: point[0], y: point[1]});
          labelElement.textContent = placeName(place.label);
          if (!isOrigin) { routeLayer.append(path, routeHit); pointLayer.append(dot, hit, labelElement); }
          const route = {...place, ...(maritime || {}), isOrigin, point, control, path, routeHit, dot, hit, labelElement};
          routes.push(route);
          for (const target of [routeHit, hit]) {
            target.addEventListener('click', () => select(route, true), {signal: routeAbort.signal});
            target.addEventListener('pointerenter', event => { if (event.pointerType !== 'touch') highlight(route); }, {signal: routeAbort.signal});
            target.addEventListener('pointerleave', () => highlight(null), {signal: routeAbort.signal});
          }
        }
        manual.forEach(place => { place.button.setAttribute('aria-pressed', 'false'); place.button.classList.remove('is-preview'); });
        beginJourney(); sizeMarkers(); refreshMotion();
      }

      function highlight(route) {
        highlighted = route || null;
        routes.forEach(item => item.button?.classList.toggle('is-preview', item === highlighted && item !== selected));
        drawScene();
      }

      function select(route, announce = false) {
        selected = route || null;
        routes.forEach(item => item.button?.setAttribute('aria-pressed', String(item === route)));
        detailCities?.querySelectorAll('[data-map-catalog-city]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.mapCatalogCity === route?.id)));
        selectedCity.textContent = placeName(route?.label || (selectedPlace?.label || (regionNames[view] || 'Europe, North America & Japan')));
        selectedCountry.textContent = placeName(route?.country || '');
        highlighted = null;
        if (route) beginJourney(route);
        else drawScene();
        if (announce && route) live.textContent = `${placeName(originName)} — ${placeName(route.label)}${route.country ? `, ${placeName(route.country)}` : ''}.`;
      }

      function viewBounds() {
        if (view === 'world') return [...world];
        const points = [origin, ...routes.flatMap(route => route.waypoints || [route.point, route.control])];
        if (points.length === 1) return [...world];
        const xs = points.map(point => point[0]);
        const ys = points.map(point => point[1]);
        const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
        const minWidth = view === 'europe' ? 190 : 400;
        const minHeight = view === 'europe' ? 160 : 230;
        const width = Math.min(1200, Math.max(minWidth, maxX - minX + 150));
        const height = Math.min(600, Math.max(minHeight, maxY - minY + 110));
        return [Math.max(0, Math.min(1200 - width, (minX + maxX) / 2 - width / 2)), Math.max(0, Math.min(600 - height, (minY + maxY) / 2 - height / 2)), width, height];
      }

      function applyCamera(next) {
        camera = next;
        svg.setAttribute('viewBox', camera.map(value => value.toFixed(3)).join(' '));
        sizeMarkers();
      }

      function frameRoutes(animate) {
        const next = viewBounds();
        if (animate && motionAllowed() && inViewport && !document.hidden && !userPaused) {
          cameraTween = {from: [...camera], to: next, start: performance.now()};
          schedule();
        } else { cameraTween = null; applyCamera(next); }
      }

      function regionItems(name = view) {
        if (!catalog) return [];
        return name === 'europe' ? catalog.countries : name === 'usa' ? catalog.states : name === 'canada' ? catalog.provinces : name === 'japan' ? catalog.japan : [];
      }

      function renderPlaceList() {
        if (!catalog || view === 'world') return;
        const items = regionItems();
        const term = searchText(search.value.trim());
        const filtered = items.filter(item => searchText(`${item.label} ${placeName(item.label)} ${item.id}`).includes(term) || item.cities?.some(city => searchText(`${city.label} ${placeName(city.label)}`).includes(term)));
        const fragment = document.createDocumentFragment();
        filtered.forEach(item => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'lrs-world-place';
          button.dataset.mapPlace = item.id;
          button.setAttribute('aria-pressed', String(selectedPlace?.id === item.id));
          button.textContent = view === 'europe' ? countryName(item.id, item.label) : placeName(item.label);
          fragment.append(button);
        });
        if (!filtered.length) {
          const empty = document.createElement('p');
          empty.className = 'lrs-world-search-empty';
          empty.textContent = t("Try another place name.");
          fragment.append(empty);
        }
        placeList.replaceChildren(fragment);
        results.textContent = t(view === 'europe' ? '{shown} of {total} countries' : view === 'canada' ? '{shown} of {total} provinces and territories' : view === 'japan' ? '{shown} of {total} cities' : '{shown} of {total} states', {shown: filtered.length, total: items.length});
        placeList.setAttribute('aria-label', t(view === 'europe' ? 'European countries' : view === 'canada' ? 'Canadian provinces and territories' : view === 'japan' ? 'Japanese cities' : 'United States'));
      }

      function choosePlace(item, animate = true, announce = true) {
        selectedPlace = item;
        detailLabel.textContent = view === 'europe' ? countryName(item.id, item.label) : placeName(item.label);
        detailCities.replaceChildren();
        if (view === 'europe') {
          detailNote.textContent = t(item.cities.length === 1 ? 'Explore the city-state.' : 'Choose a city to explore its connection.');
          const places = item.cities.map(city => ({...city, region: 'europe', country: item.label}));
          renderRoutes(places);
          places.forEach(city => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'lrs-world-city';
            button.dataset.mapCatalogCity = city.id;
            button.setAttribute('aria-pressed', 'false');
            const dot = document.createElement('span');
            dot.className = 'lrs-world-city-dot';
            dot.setAttribute('aria-hidden', 'true');
            button.append(dot, document.createTextNode(placeName(city.label)));
            detailCities.append(button);
          });
        } else {
          detailNote.textContent = '';
          renderRoutes([{...item, region: view, country: regionNames[view]}]);
        }
        placeList.querySelectorAll('[data-map-place]').forEach(button => {
          const active = button.dataset.mapPlace === item.id;
          button.setAttribute('aria-pressed', String(active));
          if (active && !announce) {
            const top = button.offsetTop - placeList.offsetTop;
            placeList.scrollTop = Math.max(0, top - placeList.clientHeight / 2 + button.offsetHeight / 2);
          }
        });
        select(routes[0] || null, false);
        frameRoutes(animate);
        if (announce) live.textContent = view === 'europe' ? t(item.cities.length === 1 ? '{country}. {count} city available.' : '{country}. {count} cities available.', {country: countryName(item.id, item.label), count: item.cities.length}) : `${placeName(originName)} — ${placeName(item.label)}, ${t(regionNames[view])}.`;
      }

      function changeView(name, animate = true) {
        if (name !== 'world' && !regionKeys.includes(name)) return;
        view = name;
        selectedPlace = null;
        viewButtons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.mapView === view)));
        if (catalog) {
          worldIntro.hidden = view !== 'world';
          browser.hidden = view === 'world';
          search.value = '';
          search.placeholder = t(view === 'usa' ? 'Search states' : view === 'canada' ? 'Search provinces or territories' : view === 'japan' ? 'Search Japanese cities' : 'Search countries or cities');
          searchLabel.textContent = search.placeholder;
          if (view !== 'world') {
            renderPlaceList();
            const items = regionItems();
            const initialId = view === 'europe' ? 'DE' : view === 'canada' ? 'ON' : view === 'japan' ? 'JP-TYO' : 'NY';
            const initial = items.find(item => item.id === initialId) || items[0];
            if (initial) { choosePlace(initial, animate, false); return; }
            detailLabel.textContent = t(regionNames[view]);
            detailNote.textContent = t("Choose another map view to explore more destinations.");
            detailCities.replaceChildren();
            renderRoutes([]);
            frameRoutes(animate);
            return;
          }
        }
        const overview = catalog ? [...catalog.countries.flatMap(country => country.cities.map(city => ({...city, region: 'europe', country: country.label}))), ...catalog.states.map(state => ({...state, region: 'usa', country: 'United States'})), ...catalog.provinces.map(province => ({...province, region: 'canada', country: 'Canada'})), ...catalog.japan.map(city => ({...city, region: 'japan', country: 'Japan'}))] : manual;
        const relevant = view === 'world' ? overview : overview.filter(place => place.region === view);
        renderRoutes(relevant);
        select(view === 'world' ? null : routes[0], false);
        frameRoutes(animate);
      }

      function canMove() { return routes.some(route => !route.isOrigin) && animationEnabled && !userPaused && motionAllowed() && inViewport && !document.hidden; }
      function schedule() {
        if (!destroyed && !frame && inViewport && !document.hidden && (canMove() || cameraTween)) frame = requestAnimationFrame(tick);
      }
      function tick(time) {
        frame = 0;
        if (destroyed) return;
        if (previousTime && canMove()) {
          const delta = Math.min(64, time - previousTime);
          elapsed += delta; journeyElapsed += delta;
          if (journeyElapsed >= duration + arrivalHold + fadeDuration + journeyPause) beginJourney();
          ambient = ambient.filter(item => elapsed - item.start < labelLifetime);
          if (ambientEnabled && elapsed >= nextLabelAt) { addAmbientLabel(); nextLabelAt = elapsed + 4400 + Math.random() * 2200; }
        }
        previousTime = time;
        if (cameraTween) {
          const t = Math.max(0, Math.min(1, (time - cameraTween.start) / 900));
          const ease = 1 - Math.pow(1 - t, 3);
          applyCamera(cameraTween.from.map((value, index) => value + (cameraTween.to[index] - value) * ease));
          if (t === 1) cameraTween = null;
        }
        if (time - lastDraw > 32 && canMove()) { drawScene(); lastDraw = time; }
        schedule();
      }

      function refreshMotion() {
        const allowed = motionAllowed() && animationEnabled;
        pause.disabled = !allowed;
        pause.setAttribute('aria-pressed', String(!allowed || userPaused));
        pauseLabel.textContent = t(allowed ? (userPaused ? 'Play' : 'Pause') : 'Still');
        pause.setAttribute('aria-label', t(allowed ? (userPaused ? 'Play map animation' : 'Pause map animation') : 'Map animation is disabled'));
        section.classList.toggle('is-running', canMove());
        if (cameraTween && (!motionAllowed() || !inViewport || document.hidden || userPaused)) {
          const target = cameraTween.to; cameraTween = null; applyCamera(target);
        }
        if (frame) cancelAnimationFrame(frame);
        frame = 0; previousTime = 0; drawScene(); refreshClockSchedule(); schedule();
      }

      function measure() {
        const bounds = viewport.getBoundingClientRect();
        viewportWidth = bounds.width || 1200;
        viewportHeight = bounds.height || 600;
        sizeMarkers();
      }

      listen(pause, 'click', () => { userPaused = !userPaused; refreshMotion(); });
      viewButtons.forEach(button => listen(button, 'click', () => changeView(button.dataset.mapView)));
      section.querySelectorAll('[data-map-explore]').forEach(button => listen(button, 'click', () => {
        changeView(button.dataset.mapExplore);
      }));
      if (search) listen(search, 'input', renderPlaceList);
      if (placeList) listen(placeList, 'click', event => {
        const button = event.target.closest('[data-map-place]');
        if (!button || !catalog) return;
        const item = regionItems().find(place => place.id === button.dataset.mapPlace);
        if (item) choosePlace(item);
      });
      if (detailCities) listen(detailCities, 'click', event => {
        const button = event.target.closest('[data-map-catalog-city]');
        if (button) select(routes.find(route => route.id === button.dataset.mapCatalogCity), true);
      });
      listen(document, 'visibilitychange', refreshMotion);
      listen(reducedMotion, 'change', refreshMotion);
      listen(window, 'resize', measure, {passive: true});
      listen(section, 'shopify:block:select', event => {
        const place = manual.find(item => item.id === event.detail?.blockId);
        if (place) {
          changeView('world', false);
          let route = routes.find(item => item.id === place.id || (Math.abs(item.lon - place.lon) < .05 && Math.abs(item.lat - place.lat) < .05));
          if (!route) { renderRoutes(manual); route = routes.find(item => item.id === place.id); }
          select(route, false);
        }
      });

      const motionObserver = new MutationObserver(refreshMotion);
      motionObserver.observe(document.body, {attributes: true, attributeFilter: ['data-lrs-motion']});
      const visibilityObserver = typeof IntersectionObserver !== 'undefined' ? new IntersectionObserver(entries => {
        inViewport = entries.some(entry => entry.isIntersecting); refreshMotion();
      }, {threshold: 0}) : null;
      visibilityObserver?.observe(viewport);
      const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
      resizeObserver?.observe(viewport);

      section.classList.add('is-enhanced');
      controls.hidden = manual.length === 0;
      if (section.dataset.destinationMode === 'custom') {
        viewButtons.forEach(button => { button.disabled = button.dataset.mapView !== 'world' && !manual.some(place => place.region === button.dataset.mapView); });
        if (!manual.some(place => place.region === view)) view = 'world';
      }
      measure();
      changeView(view, false);
      refreshMotion();
      instances.set(section, () => {
        destroyed = true;
        abort.abort(); routeAbort.abort();
        motionObserver.disconnect(); visibilityObserver?.disconnect(); resizeObserver?.disconnect();
        if (frame) cancelAnimationFrame(frame);
        if (clockInterval) clearInterval(clockInterval);
        nightPaths.forEach(path => path.removeAttribute('d'));
        if (localTime) localTime.textContent = '';
        section.classList.remove('is-enhanced', 'is-running', 'has-catalog');
        controls.hidden = true;
        if (explorer) explorer.hidden = true;
        if (fallback) fallback.hidden = false;
        routeLayer.replaceChildren(); pointLayer.replaceChildren(); travelerLayer.replaceChildren();
        manual.forEach(place => { place.button.disabled = true; });
        instances.delete(section);
      });

      if (section.dataset.destinationMode !== 'custom' && explorer && section.dataset.europeUrl && section.dataset.usaUrl) {
        const sources = [section.dataset.europeUrl, section.dataset.usaUrl, section.dataset.canadaUrl, section.dataset.japanUrl];
        Promise.allSettled(sources.map(url => url ? getCatalog(url) : Promise.resolve(null))).then(outcomes => {
          if (destroyed) return;
          const [europe, usa, remoteCanada, japan] = outcomes.map(result => result.status === 'fulfilled' ? result.value : null);
          // Keep Canada's shipped destinations available when its CDN request fails.
          let embeddedCanada = null;
          try { embeddedCanada = JSON.parse($('[data-map-canada-fallback]')?.textContent || 'null'); } catch (_) { /* Use the remote catalog when an editor changes the embedded data. */ }
          const canada = Array.isArray(remoteCanada?.provinces) && remoteCanada.provinces.some(validPlace) ? remoteCanada : embeddedCanada;
          const hasEurope = Array.isArray(europe?.countries), hasUSA = Array.isArray(usa?.states), hasCanada = Array.isArray(canada?.provinces), hasJapan = Array.isArray(japan?.cities);
          if (!hasEurope && !hasUSA && !hasCanada && !hasJapan) throw new Error('Map catalogs unavailable');
          const excludedCountries = exclusions(section.dataset.excludedCountries);
          const excludedStates = exclusions(section.dataset.excludedStates);
          const excludedProvinces = exclusions(section.dataset.excludedProvinces);
          const countries = (hasEurope ? europe.countries : []).filter(item => typeof item.id === 'string' && typeof item.label === 'string' && Array.isArray(item.cities) && !excludedCountries.has(item.id)).map(item => ({...item, cities: item.cities.filter(city => validPlace(city) && withinMapScope(city, item.id))})).filter(item => item.cities.length).sort((a, b) => a.label.localeCompare(b.label, 'en'));
          const states = (hasUSA ? usa.states : []).filter(item => validPlace(item) && !excludedStates.has(item.id)).sort((a, b) => a.label.localeCompare(b.label, 'en'));
          const provinces = (hasCanada ? canada.provinces : []).filter(item => validPlace(item) && !excludedProvinces.has(item.id)).sort((a, b) => a.label.localeCompare(b.label, 'en'));
          const japaneseCities = (hasJapan && !excludedCountries.has('JP') ? japan.cities : []).filter(item => validPlace(item) && withinMapScope(item, 'JP')).sort((a, b) => a.label.localeCompare(b.label, 'en'));
          catalog = {countries, states, provinces, japan: japaneseCities};
          controls.hidden = false;
          viewButtons.forEach(button => { button.disabled = button.dataset.mapView !== 'world' && !regionItems(button.dataset.mapView).length; });
          section.querySelectorAll('[data-map-explore]').forEach(button => { button.disabled = !regionItems(button.dataset.mapExplore).length; });
          if (view !== 'world' && !regionItems().length) view = 'world';
          $('[data-map-europe-count]').textContent = t('{count} countries', {count: countries.length});
          $('[data-map-usa-count]').textContent = t('{count} states', {count: states.length});
          const provinceCount = $('[data-map-canada-count]');
          if (provinceCount) provinceCount.textContent = t('{count} provinces and territories', {count: provinces.length});
          const japanCount = $('[data-map-japan-count]');
          if (japanCount) japanCount.textContent = t('{count} cities', {count: japaneseCities.length});
          explorer.hidden = false;
          if (fallback) fallback.hidden = true;
          section.classList.add('has-catalog');
          changeView(view, false);
        }).catch(() => {
          if (destroyed) return;
          // Authored destinations remain available if all catalogs are unavailable.
          section.classList.add('uses-map-fallback');
          viewButtons.forEach(button => { button.disabled = button.dataset.mapView !== 'world' && !manual.some(place => place.region === button.dataset.mapView); });
          if (!manual.some(place => place.region === view)) changeView('world', false);
        });
      }
    });
  }

  function destroy(root) {
    for (const [section, dispose] of instances) if (root === section || root.contains(section)) dispose();
  }
  window.LRSWorldConnections = {init, destroy};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once: true});
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => destroy(event.target));
})();
